import { useState } from "react";
import { useReportStatus } from "../../../hooks/useDashboardQueries";
import {
	DashboardReportType,
	ReportPeriod,
	ReportStatusResponse,
	StatusCounts,
} from "../../../types/dashboard.types";
import { cardStatus } from "../cardStatus";
import StackedBarChart, { StackedBarDatum } from "../charts/StackedBarChart";
import DashboardCard from "../DashboardCard";
import {
	BADGES,
	formatOwnSplit,
	REPORT_PERIOD_OPTIONS,
	REPORT_STATUS_SERIES,
	REPORT_TYPES,
	reportTypeOptions,
} from "../dashboardConfig";
import ExpandedWidgetModal from "../ExpandedWidgetModal";
import {
	MultiSelectField,
	ProjectField,
	SelectField,
	TenantField,
} from "../FilterControls";
import {
	masterTenantName,
	TenantChoice,
	TenantWidgetProps,
} from "../tenantChoice";
import {
	useDraftFilters,
	useExpandedView,
	useHasOpened,
	useTenantChoice,
} from "../widgetHooks";
import WidgetSelect from "../WidgetSelect";
import WidgetState from "../WidgetState";

const TITLE = "Report status";
const EMPTY_TEXT = "No reports in this period";
const NO_COUNTS: StatusCounts = { draft: 0, open: 0, closed: 0 };
const periodLabel = (period: ReportPeriod) =>
	REPORT_PERIOD_OPTIONS.find((option) => option.value === period)?.label ??
	period;

/** One bar per type the tenant enables, in display order; `onlyTypes` narrows them. */
const toBars = (
	data: ReportStatusResponse,
	onlyTypes: DashboardReportType[] = []
): StackedBarDatum[] =>
	REPORT_TYPES.filter(
		(type) =>
			data.types.includes(type.key) &&
			(!onlyTypes.length || onlyTypes.includes(type.key))
	).map((type) => ({
		id: type.key,
		label: type.short,
		fullLabel: type.label,
		...(data.counts[type.key] ?? NO_COUNTS),
	}));

const hasNoReports = (
	data: ReportStatusResponse,
	onlyTypes?: DashboardReportType[]
) =>
	toBars(data, onlyTypes).every(
		(bar) => !Number(bar.draft) && !Number(bar.open) && !Number(bar.closed)
	);

interface ReportStatusChartProps {
	data: ReportStatusResponse;
	onlyTypes?: DashboardReportType[];
	ownLabel: string;
	context: string;
}

function ReportStatusChart({
	data,
	onlyTypes,
	ownLabel,
	context,
}: ReportStatusChartProps) {
	const bars = toBars(data, onlyTypes);
	const summary = `${TITLE}, ${context}. ${bars
		.map(
			(bar) =>
				`${bar.fullLabel}: ${bar.draft} draft, ${bar.open} open, ${bar.closed} closed`
		)
		.join("; ")}.`;

	return (
		<StackedBarChart
			data={bars}
			series={REPORT_STATUS_SERIES}
			summary={summary}
			segmentDetail={
				data.own
					? (bar, status) =>
							formatOwnSplit(
								ownLabel,
								data.own?.[bar.id as DashboardReportType]?.[
									status as keyof StatusCounts
								] ?? 0,
								Number(bar[status])
							)
					: undefined
			}
		/>
	);
}

interface ExpandedProps {
	open: boolean;
	onClose: () => void;
	choice: TenantChoice;
	initialTenant: string;
	initialPeriod: ReportPeriod;
	ownLabel: string;
}

function ReportStatusExpanded({
	open,
	onClose,
	choice,
	initialTenant,
	initialPeriod,
	ownLabel,
}: ExpandedProps) {
	const hasOpened = useHasOpened(open);
	const filters = useDraftFilters<{
		tenant: string;
		period: ReportPeriod;
		types: DashboardReportType[];
		project?: string;
	}>({ tenant: initialTenant, period: initialPeriod, types: [] });
	const { applied, draft } = filters;
	const { target, label } = choice.resolve(applied.tenant);
	const draftTarget = choice.resolve(draft.tenant).target;
	const query = useReportStatus(hasOpened ? target : null, applied.period, {
		project: target?.scope === "tenant" ? applied.project : undefined,
	});
	const context = `${label} · ${periodLabel(applied.period)}`;

	return (
		<ExpandedWidgetModal
			open={open}
			onClose={onClose}
			badge={BADGES.reports}
			title={TITLE}
			context={context}
			onApply={filters.apply}
			onReset={filters.reset}
			filters={
				<>
					<TenantField
						value={draft.tenant}
						options={choice.options}
						onChange={(tenant) =>
							filters.update({ tenant, project: undefined })
						}
					/>
					<SelectField
						label="Period"
						value={draft.period}
						options={REPORT_PERIOD_OPTIONS}
						onChange={(period) => filters.update({ period })}
					/>
					<MultiSelectField
						label="Report type"
						options={reportTypeOptions(query.data?.types)}
						values={draft.types}
						onChange={(types) =>
							filters.update({ types: types as DashboardReportType[] })
						}
					/>
					{draftTarget?.scope === "tenant" && (
						<ProjectField
							tenantKey={draftTarget.tenantKey}
							value={draft.project}
							onChange={(project) => filters.update({ project })}
							help="Audits aren't linked to projects"
						/>
					)}
				</>
			}
		>
			<WidgetState
				status={cardStatus(query, (data) => hasNoReports(data, applied.types))}
				emptyText={EMPTY_TEXT}
				onRetry={query.refetch}
				truncated={query.data?.truncated}
			>
				{query.data && (
					<ReportStatusChart
						data={query.data}
						onlyTypes={applied.types}
						ownLabel={ownLabel}
						context={context}
					/>
				)}
			</WidgetState>
		</ExpandedWidgetModal>
	);
}

/** Reports created in the period, per type, stacked by status. */
function ReportStatusWidget({ tenants, currentTenantKey }: TenantWidgetProps) {
	const { choice, tenant, setTenant, target, label } = useTenantChoice(
		tenants,
		"master",
		currentTenantKey
	);
	const [period, setPeriod] = useState<ReportPeriod>("month");
	const expanded = useExpandedView();
	const query = useReportStatus(target, period);
	const ownLabel = masterTenantName(tenants);

	return (
		<>
			<DashboardCard
				badge={BADGES.reports}
				title={TITLE}
				className="dashboard-widget--report-status dashboard-widget--short"
				controls={
					<>
						<WidgetSelect
							value={tenant}
							options={choice.options}
							onChange={setTenant}
							ariaLabel={`Tenant for ${TITLE}`}
						/>
						<WidgetSelect
							value={period}
							options={REPORT_PERIOD_OPTIONS}
							onChange={setPeriod}
							ariaLabel={`Period for ${TITLE}`}
						/>
					</>
				}
				onExpand={expanded.show}
				status={cardStatus(query, (data) => hasNoReports(data))}
				emptyText={EMPTY_TEXT}
				forbiddenText={`You don't have access to Reporting for ${label}.`}
				onRetry={query.refetch}
				truncated={query.data?.truncated}
			>
				{query.data && (
					<ReportStatusChart
						data={query.data}
						ownLabel={ownLabel}
						context={`${label} · ${periodLabel(period)}`}
					/>
				)}
			</DashboardCard>
			<ReportStatusExpanded
				key={expanded.session}
				open={expanded.open}
				onClose={expanded.hide}
				choice={choice}
				initialTenant={tenant}
				initialPeriod={period}
				ownLabel={ownLabel}
			/>
		</>
	);
}

export default ReportStatusWidget;
